import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_2 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_3 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_4 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_5 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_6 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_7 = {
  key: 0,
  class: "text-red-500 text-sm mt-1"
}
const _hoisted_8 = { class: "flex justify-end gap-3" }

import { reactive, computed, watch, ref } from 'vue'
import { ModalComp, ActionButton, BaseInput } from '@/components'

interface DiscountFormData {
  libelle: string
  montantMin: string
  montantMax: string
  dateDebut: string
  dateFin: string
  taux: string
  actif: string
}

interface ValidationErrors {
  montantMin: string
  montantMax: string
  taux: string
  dateRange: string
}

interface Props {
  isVisible: boolean
  title: string
  discountData: DiscountFormData
  validationErrors: ValidationErrors
  submitting?: boolean
}

interface Emits {
  (e: 'update:isVisible', value: boolean): void
  (e: 'submit', data: DiscountFormData): void
  (e: 'validate'): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AddDiscountModal',
  props: {
    isVisible: { type: Boolean },
    title: {},
    discountData: {},
    validationErrors: {},
    submitting: { type: Boolean }
  },
  emits: ["update:isVisible", "submit", "validate"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

// Local copy of discount data
const localDiscount = reactive<DiscountFormData>({
  libelle: '',
  montantMin: '',
  montantMax: '',
  dateDebut: '',
  dateFin: '',
  taux: '',
  actif: 'activé'
})

// Sync with props
watch(() => props.discountData, (newData) => {
  Object.assign(localDiscount, newData)
}, { immediate: true, deep: true })

// Validation des montants
const validateMontants = () => {
  const montantMin = parseFloat(localDiscount.montantMin)
  const montantMax = parseFloat(localDiscount.montantMax)
  const taux = parseFloat(localDiscount.taux)
  
  // Réinitialiser les erreurs
  const errors = {
    montantMin: '',
    montantMax: '',
    taux: ''
  }

  // Validation montant minimum (peut être 0 comme dans efashionparis)
  if (localDiscount.montantMin && montantMin < 0) {
    errors.montantMin = 'Le montant minimum ne peut pas être négatif'
  }

  // Validation montant maximum
  if (localDiscount.montantMax && montantMax < 0) {
    errors.montantMax = 'Le montant maximum ne peut pas être négatif'
  }

  // Validation montant max > montant min
  if (localDiscount.montantMin && localDiscount.montantMax && montantMax <= montantMin) {
    errors.montantMax = 'Le montant maximum doit être supérieur au montant minimum'
  }

  // Validation du taux (pourcentage entier requis par l'API GraphQL)
  if (localDiscount.taux) {
    if (taux <= 0 || taux > 100) {
      errors.taux = 'Le taux doit être entre 1 et 100%'
    } else if (!Number.isInteger(Number(localDiscount.taux))) {
      errors.taux = 'Le taux doit être un entier (pas de décimales)'
    }
  }

  emit('validate', errors)
}

// Validation des dates
const validateDates = () => {
  let dateRangeError = ''
  
  if (localDiscount.dateDebut && localDiscount.dateFin) {
    const dateDebut = new Date(localDiscount.dateDebut)
    const dateFin = new Date(localDiscount.dateFin)
    
    if (dateFin <= dateDebut) {
      dateRangeError = 'La date de fin doit être postérieure à la date de début'
    }
  }

  emit('validate', { dateRange: dateRangeError })
}

// Validation du formulaire
const isFormValid = computed(() => {
  // Vérifier que tous les champs requis sont remplis (montantMin et montantMax sont optionnels)
  const allFieldsFilled = localDiscount.libelle && 
                         localDiscount.dateDebut && 
                         localDiscount.dateFin && 
                         localDiscount.taux

  // Vérifier que la date de fin est après la date de début
  const datesValid = localDiscount.dateDebut && localDiscount.dateFin 
    ? new Date(localDiscount.dateFin) > new Date(localDiscount.dateDebut)
    : true

  const montantsValid = !props.validationErrors.montantMin && 
                       !props.validationErrors.montantMax && 
                       !props.validationErrors.taux &&
                       !props.validationErrors.dateRange

  return allFieldsFilled && datesValid && montantsValid
})

// Local immediate guard to prevent double-clicks before parent state propagates
const localSubmitting = ref(false)

// Reset localSubmitting when parent submitting stops or modal closes
watch(() => props.submitting, (val) => {
  if (val === false) localSubmitting.value = false
})
watch(() => props.isVisible, (visible) => {
  if (!visible) localSubmitting.value = false
})

// Helpers pour contraindre les dates comme sur le sélecteur de période du dashboard
const toISO = (d: Date) => d.toISOString().slice(0, 10)
const nextDay = (d: Date) => {
  const n = new Date(d)
  n.setDate(n.getDate() + 1)
  return n
}

// Fin doit être strictement après le début
const minEndDate = computed(() => {
  if (!localDiscount.dateDebut) return ''
  const s = new Date(localDiscount.dateDebut)
  return toISO(nextDay(s))
})

// Début doit être strictement avant la fin → max start = fin - 1 jour
const maxStartDate = computed(() => {
  if (!localDiscount.dateFin) return ''
  const e = new Date(localDiscount.dateFin)
  const prev = new Date(e)
  prev.setDate(prev.getDate() - 1)
  return toISO(prev)
})

// Correction en direct: si fin <= début, ajuster fin à début + 1 jour
watch([() => localDiscount.dateDebut, () => localDiscount.dateFin], () => {
  if (!localDiscount.dateDebut || !localDiscount.dateFin) return
  const s = new Date(localDiscount.dateDebut)
  const e = new Date(localDiscount.dateFin)
  if (e <= s) {
    localDiscount.dateFin = toISO(nextDay(s))
  }
})

// Watchers pour valider en temps réel
watch([() => localDiscount.montantMin, () => localDiscount.montantMax, () => localDiscount.taux], validateMontants)
watch([() => localDiscount.dateDebut, () => localDiscount.dateFin], validateDates)

const handleSubmit = () => {
  if (props.submitting || localSubmitting.value) return
  localSubmitting.value = true
  emit('submit', { ...localDiscount })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(ModalComp), {
    isVisible: _ctx.isVisible,
    title: _ctx.title,
    size: "lg",
    "onUpdate:isVisible": _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('update:isVisible', $event)))
  }, {
    body: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "space-y-6"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", null, [
            _createVNode(_unref(BaseInput), {
              modelValue: localDiscount.libelle,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localDiscount.libelle) = $event)),
              label: "Libellé",
              required: ""
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", null, [
            _createVNode(_unref(BaseInput), {
              modelValue: localDiscount.taux,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localDiscount.taux) = $event)),
              label: "Taux de remise (%)",
              type: "number",
              min: "1",
              max: "100",
              step: "1",
              required: ""
            }, null, 8, ["modelValue"]),
            (_ctx.validationErrors.taux)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_ctx.validationErrors.taux), 1))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", null, [
            _createVNode(_unref(BaseInput), {
              modelValue: localDiscount.montantMin,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localDiscount.montantMin) = $event)),
              label: "Montant minimum",
              type: "number",
              min: "0",
              step: "0.01"
            }, null, 8, ["modelValue"]),
            (_ctx.validationErrors.montantMin)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.validationErrors.montantMin), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", null, [
            _createVNode(_unref(BaseInput), {
              modelValue: localDiscount.montantMax,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localDiscount.montantMax) = $event)),
              label: "Montant maximum",
              type: "number",
              min: "0",
              step: "0.01"
            }, null, 8, ["modelValue"]),
            (_ctx.validationErrors.montantMax)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_ctx.validationErrors.montantMax), 1))
              : _createCommentVNode("", true)
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", null, [
            _createVNode(_unref(BaseInput), {
              modelValue: localDiscount.dateDebut,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localDiscount.dateDebut) = $event)),
              label: "Date de début",
              type: "date",
              max: maxStartDate.value,
              required: ""
            }, null, 8, ["modelValue", "max"])
          ]),
          _createElementVNode("div", null, [
            _createVNode(_unref(BaseInput), {
              modelValue: localDiscount.dateFin,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localDiscount.dateFin) = $event)),
              label: "Date de fin",
              type: "date",
              min: minEndDate.value,
              required: ""
            }, null, 8, ["modelValue", "min"])
          ])
        ]),
        (_ctx.validationErrors.dateRange)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.validationErrors.dateRange), 1))
          : _createCommentVNode("", true)
      ], 32)
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_8, [
        _createVNode(_unref(ActionButton), {
          label: "Annuler",
          variant: "secondary",
          onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('update:isVisible', false)))
        }),
        _createVNode(_unref(ActionButton), {
          label: "Ajouter",
          variant: "primary",
          disabled: !isFormValid.value || _ctx.submitting || localSubmitting.value,
          loading: _ctx.submitting || localSubmitting.value,
          onClick: handleSubmit
        }, null, 8, ["disabled", "loading"])
      ])
    ]),
    _: 1
  }, 8, ["isVisible", "title"]))
}
}

})